import { HttpClient } from '@angular/common/http';
import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { Observable, catchError, finalize, of, tap } from 'rxjs';

import { ClientAccountContext, ClientAccountProperty } from '@app/core/models';
import { environment } from '@env/environment';
import { AuthStoreService } from './auth-store.service';
import { StorageService } from './storage.service';

@Injectable({ providedIn: 'root' })
export class AccountContextService {
  private readonly http = inject(HttpClient);
  private readonly authStore = inject(AuthStoreService);
  private readonly storage = inject(StorageService);
  private readonly baseUrl = `${environment.apiBaseUrl}/account-context/accounts`;

  private readonly accountsState = signal<ClientAccountContext[]>([]);
  private readonly selectedAccountIdState = signal<string | null>(null);
  private readonly loadingState = signal(false);
  private readonly revokedState = signal(false);
  private readonly contextUser = signal<unknown>(null);
  private requestVersion = 0;
  private readonly isCurrentSession = computed(() => this.contextUser() === this.authStore.user());

  readonly accounts = computed(() => this.isCurrentSession() ? this.accountsState() : []);
  readonly selectedAccountId = computed(() => this.isCurrentSession() ? this.selectedAccountIdState() : null);
  readonly selectedAccount = computed(() =>
    this.accounts().find((account) => account.clientAccountId === this.selectedAccountId()) ?? null
  );
  readonly capabilities = computed(() => this.selectedAccount()?.capabilities ?? null);
  readonly loading = computed(() => !!this.authStore.user() && (!this.isCurrentSession() || this.loadingState()));
  readonly accessRevoked = computed(() => this.isCurrentSession() && this.revokedState());

  constructor() {
    effect(() => {
      const user = this.authStore.user();
      if (!user) {
        untracked(() => this.reset());
        return;
      }

      untracked(() => this.loadAccounts().subscribe());
    }, { allowSignalWrites: true });
  }

  loadAccounts() {
    const user = this.authStore.user();
    if (!user) {
      this.reset();
      return of<ClientAccountContext[]>([]);
    }
    const version = ++this.requestVersion;
    const isCurrent = () => this.authStore.user() === user && version === this.requestVersion;
    if (!this.isCurrentSession()) {
      this.accountsState.set([]);
      this.selectedAccountIdState.set(null);
      this.revokedState.set(false);
    }
    this.contextUser.set(user);
    this.loadingState.set(true);
    return this.http.get<ClientAccountContext[]>(this.baseUrl).pipe(
      tap((accounts) => { if (isCurrent()) this.applyAccounts(accounts); }),
      catchError(() => {
        if (isCurrent()) this.applyAccounts([]);
        return of<ClientAccountContext[]>([]);
      }),
      finalize(() => { if (isCurrent()) this.loadingState.set(false); })
    );
  }

  selectAccount(clientAccountId: string): boolean {
    const account = this.accounts().find((item) => item.clientAccountId === clientAccountId);
    if (!account) {
      this.revokedState.set(true);
      this.loadAccounts().subscribe();
      return false;
    }

    this.selectedAccountIdState.set(account.clientAccountId);
    this.storage.setSelectedClientAccountId(account.clientAccountId);
    this.storage.clearSelectedPropertyId();
    this.revokedState.set(false);
    return true;
  }

  getProperties(clientAccountId = this.selectedAccountId()) {
    if (!clientAccountId || !this.accounts().some((account) => account.clientAccountId === clientAccountId)) {
      return of<ClientAccountProperty[]>([]);
    }

    return this.http.get<ClientAccountProperty[]>(`${this.baseUrl}/${clientAccountId}/properties`);
  }

  refreshSelectedAccount(): Observable<ClientAccountContext | ClientAccountContext[] | null> {
    const selectedAccountId = this.selectedAccountId();
    const user = this.authStore.user();
    const version = this.requestVersion;
    const isCurrent = () => user === this.authStore.user() && version === this.requestVersion && selectedAccountId === this.selectedAccountId();
    if (!selectedAccountId) {
      return this.loadAccounts();
    }

    return this.http.get<ClientAccountContext>(`${this.baseUrl}/${selectedAccountId}`).pipe(
      tap((account) => {
        if (!isCurrent()) return;
        this.accountsState.update((accounts) =>
          accounts.map((item) => item.clientAccountId === account.clientAccountId ? account : item)
        );
        this.revokedState.set(false);
      }),
      catchError(() => {
        if (!isCurrent()) return of(null);
        this.revokedState.set(true);
        this.storage.clearSelectedClientAccountId();
        this.selectedAccountIdState.set(null);
        this.loadAccounts().subscribe();
        return of<ClientAccountContext | null>(null);
      })
    );
  }

  reset(): void {
    this.requestVersion++;
    this.contextUser.set(null);
    this.accountsState.set([]);
    this.selectedAccountIdState.set(null);
    this.loadingState.set(false);
    this.revokedState.set(false);
  }

  private applyAccounts(accounts: ClientAccountContext[]): void {
    this.accountsState.set(accounts);
    const persisted = this.storage.getSelectedClientAccountId();
    const selected = accounts.find((account) => account.clientAccountId === persisted) ?? accounts[0] ?? null;

    if (!selected) {
      this.selectedAccountIdState.set(null);
      this.storage.clearSelectedClientAccountId();
      return;
    }

    this.selectedAccountIdState.set(selected.clientAccountId);
    this.storage.setSelectedClientAccountId(selected.clientAccountId);
    this.revokedState.set(!!persisted && persisted !== selected.clientAccountId);
  }
}
